import { cn } from '@/shared/helpers/classnames'
import {
  extractResponsiveInitialValue,
  extractResponsiveValue,
} from '@/shared/ui/layouts/responsive-utils'

import React from 'react'

import { flexLayoutVariants } from '../flexLayoutVariants'

import { VLayoutProps } from './types'

/**
 * VLayout - Composant de layout vertical flexible et responsive.
 * Permet d'aligner des éléments verticalement avec gestion du gap, alignement, justification, self et wrap responsive.
 *
 * @example
 * // Layout simple vertical
 * <VLayout gap="4" items="center" justify="between">
 *   <div>Élément 1</div>
 *   <div>Élément 2</div>
 * </VLayout>
 *
 * @example
 * // Layout responsive avec wrap
 * <VLayout
 *   gap={{ initial: '2', md: '4', lg: '5' }}
 *   items={{ initial: 'start', md: 'center' }}
 *   justify={{ initial: 'start', lg: 'between' }}
 *   wrap={{ initial: 'wrap', md: 'nowrap' }}
 * >
 *   <div>Élément 1</div>
 *   <div>Élément 2</div>
 * </VLayout>
 *
 * ### Breakpoints disponibles
 * - `initial` : Valeur par défaut (tous les écrans)
 * - `sm` : Small (<= 640px) - applique `sm:` en max-width
 * - `md` : Medium (<= 768px) - applique `md:` en max-width
 * - `lg` : Large (<= 1024px) - applique `lg:` en max-width
 * - `xl` : Extra Large (<= 1280px) - applique `xl:` en max-width
 *
 * **Note:** Les breakpoints utilisent des requêtes max-width (max-sm:, max-md:, max-lg:, max-xl:)
 * ce qui signifie qu'un breakpoint s'applique jusqu'à cette largeur, pas à partir de cette largeur.
 *
 * @param gap Espacement vertical entre les enfants (responsive)
 * @param items Alignement horizontal des enfants (responsive)
 * @param justify Justification verticale des enfants (responsive)
 * @param self Alignement individuel du composant (responsive)
 * @param wrap Comportement de retour à la ligne des enfants (responsive)
 * @param children Contenu à afficher dans le layout
 */
export const VLayout: React.FC<VLayoutProps> = ({ gap, items, justify, self, wrap, children }) => {
  // Extraction des valeurs initial
  const gapInitialValue = extractResponsiveInitialValue(gap)
  const itemsInitialValue = extractResponsiveInitialValue(items)
  const justifyInitialValue = extractResponsiveInitialValue(justify)
  const selfInitialValue = extractResponsiveInitialValue(self)
  const wrapInitialValue = extractResponsiveInitialValue(wrap)

  // Extraction des valeurs responsives pour chaque breakpoint
  const gapSm = extractResponsiveValue('sm', gap)
  const gapMd = extractResponsiveValue('md', gap)
  const gapLg = extractResponsiveValue('lg', gap)
  const gapXl = extractResponsiveValue('xl', gap)

  const itemsSm = extractResponsiveValue('sm', items)
  const itemsMd = extractResponsiveValue('md', items)
  const itemsLg = extractResponsiveValue('lg', items)
  const itemsXl = extractResponsiveValue('xl', items)

  const justifySm = extractResponsiveValue('sm', justify)
  const justifyMd = extractResponsiveValue('md', justify)
  const justifyLg = extractResponsiveValue('lg', justify)
  const justifyXl = extractResponsiveValue('xl', justify)

  const selfSm = extractResponsiveValue('sm', self)
  const selfMd = extractResponsiveValue('md', self)
  const selfLg = extractResponsiveValue('lg', self)
  const selfXl = extractResponsiveValue('xl', self)

  const wrapSm = extractResponsiveValue('sm', wrap)
  const wrapMd = extractResponsiveValue('md', wrap)
  const wrapLg = extractResponsiveValue('lg', wrap)
  const wrapXl = extractResponsiveValue('xl', wrap)

  const className = flexLayoutVariants({
    direction: 'col',
    gap: gapInitialValue,
    items: itemsInitialValue,
    justify: justifyInitialValue,
    self: selfInitialValue,
    wrap: wrapInitialValue,
    gapSm,
    gapMd,
    gapLg,
    gapXl,
    itemsSm,
    itemsMd,
    itemsLg,
    itemsXl,
    justifySm,
    justifyMd,
    justifyLg,
    justifyXl,
    selfSm,
    selfMd,
    selfLg,
    selfXl,
    wrapSm,
    wrapMd,
    wrapLg,
    wrapXl,
  })

  return <div className={cn(className)}>{children}</div>
}

VLayout.displayName = 'VLayout'
